import { useTranslation } from 'react-i18next'
import { ChartLine, LayoutGrid, RotateCcw, Rows3, Table2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  STAT_METRIC_CATALOG,
  STAT_METRIC_GROUPS,
  formatStatValue,
  isCurrencyAmount,
  type StatMetricFormat,
  type StatMetricGroup,
} from './statsColumns'
import type { StatsTotals } from '@/services/stats/statsApi'
import { useStatLabel } from '@/hooks/useStatLabel'
import type { StatDisplay } from '@/hooks/useStatsLayout'

interface AllStatsPopoverProps {
  /** The period's totals, exactly as the stats API reported them. */
  totals: StatsTotals
  /** Metrics the caller can plot; each gets a toggle that puts it in the chart. */
  chartableKeys?: string[]
  /** The metrics plotted now, each with the colour of its line. */
  chartColors?: Record<string, string>
  onToggleChart?: (key: string) => void
  /** Metrics the caller can place on its panel, as a tile or a row in its table. */
  placeableKeys?: string[]
  displayOf?: (key: string) => StatDisplay
  onDisplayChange?: (key: string, display: StatDisplay) => void
  /** Offers going back to the default layout, once something was moved. */
  onResetLayout?: () => void
  /** Extra classes for the trigger, so callers can place it in their own row. */
  className?: string
}

/** Keys of the totals envelope that are not metrics anyone wants to read. */
const NOT_A_METRIC = new Set(['currency', 'chunkStart', 'chunkEnd', 'start', 'end'])

/**
 * A metric with no entry in the catalog still gets a readable name — better a
 * spaced-out field name than hiding a number the API bothered to report.
 */
function humanize(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/** One of a row's toggles, filled with `color` while it is on, like a selected tile. */
function RowToggle({
  icon: Icon,
  label,
  pressed,
  color = 'var(--primary)',
  onClick,
}: {
  icon: LucideIcon
  label: string
  pressed: boolean
  color?: string
  onClick: () => void
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={label}
          aria-pressed={pressed}
          className={pressed ? undefined : 'text-muted-foreground'}
          style={pressed ? { color: 'var(--card)', background: color } : undefined}
          onClick={onClick}
        >
          <Icon />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

/**
 * Every number the stats API reported for a period, behind one button — the
 * card shows the handful worth a tile, this shows them all in one place, and
 * is where any of them can be given a tile or a row of its own.
 */
export function AllStatsPopover({
  totals,
  chartableKeys = [],
  chartColors = {},
  onToggleChart,
  placeableKeys = [],
  displayOf,
  onDisplayChange,
  onResetLayout,
  className,
}: AllStatsPopoverProps) {
  const { t } = useTranslation()
  const statLabel = useStatLabel()
  const chartable = new Set(onToggleChart ? chartableKeys : [])
  const canPlace = !!displayOf && !!onDisplayChange
  const placeable = new Set(canPlace ? placeableKeys : [])
  // A column per toggle, headed with what it toggles, so the buttons read
  // without hovering each one.
  const columnCount = 2 + (canPlace ? 2 : 0) + (onToggleChart ? 1 : 0)
  // Clicking where a metric already is takes it off the panel again.
  const place = (key: string, display: StatDisplay) =>
    onDisplayChange?.(key, displayOf?.(key) === display ? 'hidden' : display)

  // Catalog order first, so the list reads the same way every time, then
  // anything the API reported that the catalog has not heard of yet.
  const catalogKeys = Object.keys(STAT_METRIC_CATALOG)
  const extraKeys = Object.keys(totals).filter((key) => !catalogKeys.includes(key))
  const rows = [...catalogKeys, ...extraKeys].flatMap((key) => {
    if (NOT_A_METRIC.has(key)) return []
    const value = totals[key]
    // Only numbers and money are values; anything else is envelope.
    if (typeof value !== 'number' && !isCurrencyAmount(value)) return []
    const metric = STAT_METRIC_CATALOG[key]
    const format: StatMetricFormat = metric?.format ?? 'decimal'
    const text = formatStatValue(value, format)
    if (text === null) return []
    return [
      {
        key,
        group: metric?.group,
        label: metric ? statLabel(metric.labelKey) : humanize(key),
        value: text,
      },
    ]
  })

  // A group with nothing reported in the period is not worth a heading, and
  // metrics the catalog does not place end up together under "other".
  const sections = [
    ...STAT_METRIC_GROUPS.map((group) => ({
      key: group.key as StatMetricGroup | 'other',
      label: t(group.labelKey),
      rows: rows.filter((row) => row.group === group.key),
    })),
    { key: 'other' as const, label: t('stats.groupOther'), rows: rows.filter((row) => !row.group) },
  ].filter((section) => section.rows.length > 0)

  if (sections.length === 0) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className={className}>
          <Table2 className="size-4 opacity-50" />
          {t('stats.showAllStats')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-104 p-0">
        <div className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">
          {t('stats.allStats')}
        </div>
        <Table containerClassName="max-h-96 overflow-y-auto">
          <TableHeader className="sticky top-0 z-10 bg-popover">
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-8 text-xs">{t('stats.metric')}</TableHead>
              <TableHead className="h-8 text-right text-xs">{t('stats.value')}</TableHead>
              {canPlace && (
                <>
                  <TableHead className="h-8 w-0 px-1 text-center text-xs">
                    {t('stats.columnCard')}
                  </TableHead>
                  <TableHead className="h-8 w-0 px-1 text-center text-xs">
                    {t('stats.columnTable')}
                  </TableHead>
                </>
              )}
              {onToggleChart && (
                <TableHead className="h-8 w-0 pr-2 pl-1 text-center text-xs">
                  {t('stats.columnChart')}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          {sections.map((section) => (
            <TableBody key={section.key}>
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columnCount}
                  className="bg-muted/50 py-1 text-[0.6875rem] font-medium text-muted-foreground"
                >
                  {section.label}
                </TableCell>
              </TableRow>
              {section.rows.map((row) => {
                const color = chartColors[row.key]
                const display = displayOf?.(row.key)
                return (
                  <TableRow key={row.key}>
                    <TableCell className="text-xs text-muted-foreground">{row.label}</TableCell>
                    <TableCell className="text-right text-xs font-medium tabular-nums">
                      {row.value}
                    </TableCell>
                    {canPlace && (
                      <>
                        <TableCell className="w-0 px-1 py-1 text-center">
                          {placeable.has(row.key) && (
                            <RowToggle
                              icon={LayoutGrid}
                              label={t('stats.showAsCard')}
                              pressed={display === 'card'}
                              onClick={() => place(row.key, 'card')}
                            />
                          )}
                        </TableCell>
                        <TableCell className="w-0 px-1 py-1 text-center">
                          {placeable.has(row.key) && (
                            <RowToggle
                              icon={Rows3}
                              label={t('stats.showInTable')}
                              pressed={display === 'table'}
                              onClick={() => place(row.key, 'table')}
                            />
                          )}
                        </TableCell>
                      </>
                    )}
                    {onToggleChart && (
                      <TableCell className="w-0 py-1 pr-2 pl-1 text-center">
                        {chartable.has(row.key) && (
                          <RowToggle
                            icon={ChartLine}
                            label={t('stats.showInChart')}
                            pressed={!!color}
                            color={color}
                            onClick={() => onToggleChart(row.key)}
                          />
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          ))}
        </Table>
        {onResetLayout && (
          <div className="border-t p-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={onResetLayout}
            >
              <RotateCcw className="size-4" />
              {t('stats.resetLayout')}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
